import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreatedBy, Reffable, SystemActors, WithRef } from '../interfaces';
import { type PartialWithFieldValue } from './field-value';
import {
  CollectionReference,
  DocumentReference,
  FieldValue,
  DocumentSnapshot,
  Query,
  QueryConstraint,
  SetOptions,
  Transaction,
  doc as docFirestore,
  docExists,
  getDoc as getDocFirestore,
  increment,
  query as queryFirestore,
} from './adaptor';
import { asColRef, firstResult, getCount, query$ } from './collection';
import {
  IDocUpdateOptions,
  UnsavedDocument,
  addDoc,
  asDocRef,
  deleteDoc,
  doc$,
  getDoc,
  getDocs,
  getParentDocRef,
  patchDoc,
  safeSnapshotToWithRef,
  saveDoc,
  updateDoc,
  upsertDoc,
} from './document';
import { runTransaction, type ITransactionOptions } from './firestore-resolver';
import { fromDocRef } from './from-ref';
import { DatabaseUsageTracker } from './usage-tracking';

export class Firestore {
  static getParentDocRef<T extends object>(
    path: string | DocumentReference
  ): DocumentReference<T> {
    return getParentDocRef(path);
  }

  static getDoc<T extends object>(
    ref: DocumentReference<T>,
    transaction?: Transaction
  ): Promise<WithRef<T>> {
    return getDoc(ref, transaction);
  }

  static async safeGetDoc<T extends object>(
    ref: DocumentReference<T>,
    transaction?: Transaction
  ): Promise<WithRef<T> | undefined> {
    const snapshot = await (transaction
      ? transaction.get(ref)
      : getDocFirestore(ref));
    DatabaseUsageTracker.track(snapshot.ref.parent.path, 'get', 'document');
    return safeSnapshotToWithRef<T>(snapshot);
  }

  static getDocs<T extends object>(
    col: CollectionReference<T> | Query<T>,
    ...queryConstraints: QueryConstraint[]
  ): Promise<WithRef<T>[]> {
    return getDocs(col, ...queryConstraints);
  }

  static async addDoc<T extends object>(
    collection: CollectionReference<T>,
    item: T,
    uid?: string | DocumentReference<T>,
    transaction?: Transaction,
    updatedBy: CreatedBy = SystemActors.Unknown
  ): Promise<DocumentReference<T>> {
    return addDoc(collection, item, uid, transaction, updatedBy);
  }

  static async patchDoc<T>(
    ref: DocumentReference<T>,
    data: PartialWithFieldValue<T>,
    transaction?: Transaction,
    options?: IDocUpdateOptions,
    updatedBy: CreatedBy = SystemActors.Unknown
  ): Promise<DocumentReference<Partial<T>>> {
    return patchDoc(ref, data, transaction, options, updatedBy);
  }

  /**
   * Writes at a known id without clobbering an existing document. `addDoc` is a
   * bare `setDoc`, so a caller that reads-then-creates will overwrite whatever
   * another writer created in between; this patches instead when the document
   * already exists.
   */
  static async upsertDoc<T extends object>(
    collection: CollectionReference<T>,
    item: T,
    uid?: string | DocumentReference<T>,
    transaction?: Transaction,
    options?: IDocUpdateOptions,
    updatedBy: CreatedBy = SystemActors.Unknown
  ): Promise<DocumentReference<T>> {
    return upsertDoc(collection, item, uid, transaction, options, updatedBy);
  }

  static async updateDoc<T extends object>(
    ref: DocumentReference<T>,
    data: PartialWithFieldValue<T>,
    transaction?: Transaction,
    updatedBy: CreatedBy = SystemActors.Unknown
  ): Promise<void> {
    return updateDoc(ref, data, transaction, updatedBy);
  }

  static async saveDoc<T extends object>(
    item: Reffable<T> | UnsavedDocument<T>,
    options?: SetOptions,
    transaction?: Transaction,
    updateOptions?: IDocUpdateOptions,
    updatedBy: CreatedBy = SystemActors.Unknown
  ): Promise<DocumentReference<T>> {
    return saveDoc(item, options, transaction, updateOptions, updatedBy);
  }

  static async deleteDoc<T>(
    ref: DocumentReference<T>,
    transaction?: Transaction,
    updatedBy: CreatedBy = SystemActors.Unknown,
    data?: Partial<T>
  ): Promise<void> {
    return deleteDoc(ref, transaction, updatedBy, data);
  }

  static doc$<T extends object>(
    ref: DocumentReference<T> | string
  ): Observable<WithRef<T>> {
    return doc$(ref);
  }

  static async getCount(queryFn: Query): Promise<number> {
    return getCount(queryFn);
  }

  static async firstResult<T extends object>(
    queryFn: Query<T>,
    ...queryConstraints: QueryConstraint[]
  ): Promise<WithRef<T> | undefined> {
    return firstResult(queryFn, ...queryConstraints);
  }

  static runTransaction<T>(
    updateFunction: (transaction: Transaction) => Promise<T>,
    options?: ITransactionOptions
  ): Promise<T> {
    return runTransaction(updateFunction, options);
  }

  /** Uses the adaptor's `doc()`; `collection.doc()` is a Node-only admin SDK method. */
  static doc<T extends object>(
    collection: CollectionReference<T>,
    id: string
  ): DocumentReference<T> {
    return docFirestore(collection, id);
  }

  static docExists<T extends object>(
    ref: DocumentReference<T> | DocumentSnapshot<T>
  ): Promise<boolean> {
    return docExists(ref);
  }

  static safeDoc$<T extends object>(
    ref: DocumentReference<T> | string
  ): Observable<WithRef<T> | undefined> {
    return fromDocRef(asDocRef<T>(ref)).pipe(
      map((docSnapshot) => safeSnapshotToWithRef<T>(docSnapshot.payload))
    );
  }

  static query$<T extends object>(
    colRef: CollectionReference<T> | Query<T> | string,
    ...queryConstraints: QueryConstraint[]
  ): Observable<WithRef<T>[]> {
    return query$(colRef, ...queryConstraints);
  }

  static isCreatedByMigration<T extends object>(item: WithRef<T>): boolean {
    return item.createdBy === SystemActors.Migration;
  }

  static isUpdatedByMigration<T extends object>(item: WithRef<T>): boolean {
    return item.updatedBy === SystemActors.Migration;
  }

  static increment(value: number): FieldValue {
    return increment(value);
  }

  static query<T extends object>(
    col: CollectionReference<T> | Query<T> | string,
    ...queryConstraints: QueryConstraint[]
  ): Query<T> {
    const colRef = asColRef<T>(col);
    return queryFirestore(colRef, ...queryConstraints);
  }
}
